import { maximumLevelNumber, minimumLevelNumber } from './levels';

export interface GameProgress {
  readonly currentLevel: number;
  readonly highestClearedLevel: number;
  readonly totalWins: number;
  readonly cleanStreak: number;
}

export const progressStorageKey = 'arrows.progress.v1';

const initialProgress: GameProgress = {
  currentLevel: minimumLevelNumber,
  highestClearedLevel: 0,
  totalWins: 0,
  cleanStreak: 0,
};

let memoryProgress = initialProgress;
let storageDisabled = false;

function availableStorage(): Storage | null {
  if (storageDisabled) {
    return null;
  }
  try {
    return globalThis.localStorage ?? null;
  } catch {
    storageDisabled = true;
    return null;
  }
}

function isLevelNumber(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= minimumLevelNumber &&
    Number(value) <= maximumLevelNumber;
}

type StoredProgress = Omit<GameProgress, 'cleanStreak'> & { readonly cleanStreak?: number };

function isProgress(value: unknown): value is StoredProgress {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<GameProgress>;
  return isLevelNumber(candidate.currentLevel) &&
    Number.isInteger(candidate.highestClearedLevel) &&
    Number(candidate.highestClearedLevel) >= 0 &&
    Number(candidate.highestClearedLevel) <= maximumLevelNumber &&
    Number.isInteger(candidate.totalWins) && Number(candidate.totalWins) >= 0 &&
    (candidate.cleanStreak === undefined ||
      (Number.isInteger(candidate.cleanStreak) && Number(candidate.cleanStreak) >= 0));
}

function persist(progress: GameProgress): GameProgress {
  memoryProgress = progress;
  const storage = availableStorage();
  if (storage !== null) {
    try {
      storage.setItem(progressStorageKey, JSON.stringify(progress));
    } catch {
      storageDisabled = true;
    }
  }
  return progress;
}

export function loadProgress(): GameProgress {
  const storage = availableStorage();
  if (storage === null) {
    return memoryProgress;
  }

  let serialized: string | null;
  try {
    serialized = storage.getItem(progressStorageKey);
  } catch {
    storageDisabled = true;
    return memoryProgress;
  }

  if (serialized === null) {
    memoryProgress = initialProgress;
    return memoryProgress;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    parsed = null;
  }
  if (isProgress(parsed)) {
    memoryProgress = { ...parsed, cleanStreak: parsed.cleanStreak ?? 0 };
    return memoryProgress;
  }
  try {
    storage.removeItem(progressStorageKey);
  } catch {
    // The invalid persisted value is ignored even when it cannot be removed.
  }

  memoryProgress = initialProgress;
  return memoryProgress;
}

export function recordClear(levelNumber: number, cleanClear: boolean): GameProgress {
  if (!isLevelNumber(levelNumber)) {
    throw new RangeError(`levelNumber must be between ${minimumLevelNumber} and ${maximumLevelNumber}.`);
  }
  const progress = loadProgress();
  return persist({
    currentLevel: Math.min(levelNumber + 1, maximumLevelNumber),
    highestClearedLevel: Math.max(progress.highestClearedLevel, levelNumber),
    totalWins: progress.totalWins + 1,
    cleanStreak: cleanClear ? progress.cleanStreak + 1 : 0,
  });
}

export function setCurrentLevel(levelNumber: number): GameProgress {
  if (!isLevelNumber(levelNumber)) {
    throw new RangeError(`levelNumber must be between ${minimumLevelNumber} and ${maximumLevelNumber}.`);
  }
  const progress = loadProgress();
  return persist({ ...progress, currentLevel: levelNumber });
}

export function mergeServerProgress(server: {
  readonly highestClearedLevel: number;
  readonly totalWins: number;
}): GameProgress {
  const progress = loadProgress();
  return persist({
    ...progress,
    highestClearedLevel: Math.min(
      maximumLevelNumber,
      Math.max(progress.highestClearedLevel, server.highestClearedLevel),
    ),
    totalWins: Math.max(progress.totalWins, server.totalWins),
  });
}
